import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Button, Chip } from '@mui/material';
import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import ConfirmDialog from 'components/confirm-dialog/ConfirmDialog';
import InsideLoading from 'components/loading/InsideLoading';
import InsideSaving from 'components/loading/InsideSaving';
import NoData from 'components/no-data/NoData';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  RecordNote,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';
import { moneyFormat } from 'helpers/moneyFormat';
import { api } from 'services/api';
import history from 'services/history';
import { Package, PackageProduct } from 'types/package';
import PackageProducts from '../PackageProducts';
import PackageForm from '../form/PackageForm';
import PackageTab from '../tab/PackageTab';
import { usePackageValidation } from '../validation/usePackageValidation';

// todos os campos validados ficam na aba "Dados do pacote"
const FORM_TAB = 'main';

const EditPackage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [validation, setValidation, validate] = usePackageValidation();
  const [tab, setTab] = useState(FORM_TAB);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pkg, setPkg] = useState<Package | null>(null);
  const [savedName, setSavedName] = useState('');
  const [savedActive, setSavedActive] = useState(true);

  const loadPackage = useCallback(() => {
    if (!id) return Promise.resolve();

    return api
      .get(`/packages/${id}`)
      .then(response => {
        setPkg(response.data);
        setSavedName(response.data.name);
        setSavedActive(!!response.data.active);
      })
      .catch(err => {
        console.error(err);
        toast.error('Não foi possível carregar os dados do pacote');
        history.push('/packages');
      });
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadPackage().finally(() => setLoading(false));
  }, [loadPackage]);

  // a validação guarda o erro do campo; abre a aba onde ele está
  useEffect(() => {
    if (Object.keys(validation).length > 0) setTab(FORM_TAB);
  }, [validation]);

  function goBack() {
    history.push('/packages');
  }

  function handleChange(index: keyof Package, value: any) {
    setPkg(prev => (prev ? { ...prev, [index]: value } : null));
  }

  function handleSubmit() {
    if (!pkg) return;

    setSaving(true);

    // os produtos são gravados na hora, na aba deles
    api
      .put(`/packages/${pkg.id}`, { ...pkg, products: [] })
      .then(() => loadPackage())
      .then(() => toast.success('Pacote atualizado'))
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível atualizar o pacote');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    if (!pkg) return;

    validate(pkg)
      .then(() => {
        handleSubmit();
        setValidation({});
      })
      .catch((err: yup.ValidationError) => {
        if (err instanceof yup.ValidationError) {
          setValidation({
            [err.path as string]: err.message,
          });
        }
      });
  }

  // descarta o que foi digitado e recarrega o cadastro
  function handleCancel() {
    setValidation({});
    setLoading(true);
    loadPackage().finally(() => setLoading(false));
  }

  // "excluir" só inativa o pacote
  function handleInactivatePackage() {
    if (!pkg) return;

    setSaving(true);

    api
      .delete(`/packages/${pkg.id}`)
      .then(() => {
        toast.success('Pacote inativado com sucesso!');
        history.push('/packages');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível inativar o pacote');
      })
      .finally(() => {
        setSaving(false);
        setConfirmDialogOpen(false);
      });
  }

  // produtos são gravados na hora, enviando só o item alterado
  function saveProducts(products: PackageProduct[], failure: string) {
    if (!pkg) return;

    api
      .put(`/packages/${pkg.id}`, { ...pkg, products })
      .then(response => {
        toast.success('Salvo');
        setPkg(state => (state ? { ...state, products: response.data.products } : null));
      })
      .catch(err => {
        toast.error(err.response?.data?.error || failure);
      });
  }

  function handleChangeAddProduct(product: PackageProduct) {
    saveProducts([product], 'Não foi possível adicionar o produto ao pacote');
  }

  function handleDeleteProduct(product: PackageProduct) {
    saveProducts([{ ...product, active: 0 } as PackageProduct], 'Não foi possível excluir o produto do pacote');
  }

  const isEditing = tab === FORM_TAB;

  return (
    <>
      <Appbar title="Pacotes" backAction={goBack} />

      {saving && <InsideSaving />}

      {confirmDialogOpen && (
        <ConfirmDialog
          confirmLabel="Inativar"
          destructive
          handleNoClick={() => setConfirmDialogOpen(false)}
          handleYesClick={handleInactivatePackage}
          title="Deseja inativar esse pacote?"
        />
      )}

      <PageShell>
        <RecordHeader title={savedName || 'Pacote'} active={savedActive} backAction={goBack}>
          {pkg?.supplier && (
            <Chip
              size="small"
              variant="outlined"
              label={pkg.supplier.company_trade_name || pkg.supplier.company_name}
            />
          )}
          {pkg && <Chip size="small" variant="outlined" label={`Custo ${moneyFormat(Number(pkg.cost_price) || 0)}`} />}
        </RecordHeader>

        <WorkArea>
          <PackageTab onChange={newTab => setTab(newTab)} tab={tab} productsCount={pkg?.products.length} />
          <ContentPad>
            {loading ? (
              <InsideLoading />
            ) : !pkg ? (
              <NoData message="Não foi possível carregar os dados do pacote" />
            ) : tab === 'products' ? (
              <>
                <PackageProducts
                  pkg={pkg}
                  handleDeleteProduct={handleDeleteProduct}
                  handleChangeAddProduct={handleChangeAddProduct}
                />
                <RecordNote>Adicionar, editar e excluir produto salva na hora.</RecordNote>
              </>
            ) : (
              <StyledForm onSubmit={handleValidation}>
                <PackageForm handleChange={handleChange} pkg={pkg} validation={validation} />
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        {isEditing && !loading && pkg && (
          <RecordFooter sx={{ justifyContent: 'space-between' }}>
            <Button color="error" size="small" disabled={saving} onClick={() => setConfirmDialogOpen(true)}>
              Inativar pacote
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="outlined" color="inherit" size="small" disabled={saving} onClick={handleCancel}>
                Cancelar
              </Button>
              <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
                Salvar alterações
              </Button>
            </div>
          </RecordFooter>
        )}
      </PageShell>
    </>
  );
};

export default EditPackage;
